import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { Action } from '@ngrx/store';
import { Inject, Injectable } from '@angular/core';
import { ActionWithCorrelationAttributes } from '@kion/ngrx-helper';

interface CacheItem<T extends Action> {
  pureActionId$: BehaviorSubject<string|null>,
  isCaching$:  BehaviorSubject<boolean>,
  isCachingError$: BehaviorSubject<boolean>,
  cachedResultAction$: BehaviorSubject<Observable<T>|null>,
  expiresAt: number,
  originalTtl: number,
}

export interface ActionCacheOptions {
  ttl?: number
}

export interface ConfigOptions {
  ttl?: number,
  logEnabled?: boolean
}

@Injectable({
  providedIn: 'root'
})
export class ActionCache {

  protected defaultTtl = 10000;

  protected logEnabled = false;

  protected items: CacheItem<Action>[] = [];

  constructor(@Inject('ConfigOptions')options?: ConfigOptions) {
    if (options.ttl) {
      this.defaultTtl = options.ttl;
    }
    if (options.logEnabled) {
      this.logEnabled = options.logEnabled;
    }
  }

  protected findOrCreateCacheEntry<T extends Action>(action, options: ActionCacheOptions): CacheItem<T> {
    const indexAction = {...action};
    if (indexAction.correlationParams) {
      delete indexAction.correlationParams;
    }

    const indexSignature = JSON.stringify(indexAction);
    const ttl = (options.ttl || this.defaultTtl);

    if (this.items[indexSignature]) {
      const item = this.items[indexSignature];
      if (item.expiresAt < new Date().getTime()) {
        delete this.items[indexSignature];
      }
    }
    if (this.items[indexSignature] === undefined) {
      this.items[indexSignature] = {
        pureActionId$: new BehaviorSubject<string|null>(null),
        isCaching$: new BehaviorSubject<boolean>(false),
        isCachingError$: new BehaviorSubject<boolean>(false),
        cachedResultAction$: new BehaviorSubject<Observable<Action>|null>(null),
        expiresAt: new Date().getTime() + ttl,
        originalTtl: ttl
      }
    }
    return this.items[indexSignature];
  }

  public clear() { this.items = []; }

  public withCache<T extends Action>(action: ActionWithCorrelationAttributes, pure: () => Observable<T>, cached: (successAction: Observable<T>) => Observable<T>, options: ActionCacheOptions = {}): Observable<Action> {
    const actionId = action.type + '_' + action.__correlationId;
    const cacheItem = this.findOrCreateCacheEntry<T>(action, options);
    let subscriptionStopped = false;

    return combineLatest(
      [
        cacheItem.pureActionId$,
        cacheItem.cachedResultAction$
      ]
    ).pipe(
      filter(() => subscriptionStopped == false),
      switchMap(([pureActionId, resultAction$]) => {
        if (resultAction$) {
          if (this.logEnabled) {
            console.log('ActionCache:cached', action);
          }
          return cached(resultAction$);
        }

        if (pureActionId == null) {
          subscriptionStopped = true;
          cacheItem.pureActionId$.next(actionId);
          pureActionId = actionId;
        }

        if (pureActionId == actionId) {
          return pure().pipe(
            catchError(
              (error) => {
                cacheItem.pureActionId$.next(null);
                return throwError(error);
              }
            ),
            tap((r) => cacheItem.cachedResultAction$.next(of(r)))
          );
        }

        return of({ type: '--nonse--' }, );
      }),
      filter((action) => action.type != '--nonse--'),
      take(1)
    );
  }
}
